import React, { useEffect, useState } from "react";

import {
	Button,
	FileViewer,
	InfoMessage,
	Input,
	Modal,
} from "@amphore-dev/amphore-lib";
import { useFormikContext } from "formik";
import { t } from "i18next";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { pdfjs } from "react-pdf";
import { useNavigate } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import ReportsServices from "@/services/ReportsService";

import { useFiltersContext } from "@/hooks/useFiltersContext";

import {
	ClientSelect,
	CompanySelect,
	WorkTypesSelect,
} from "@/components/molecules";

import { reportKeys } from "@/constants/CQueryKeys";

import {
	TReportFormProject,
	TReportFormValues,
	TReportSection,
	TSavedReport,
} from "@/types";

import { promiseToast } from "@/modules/index";
import { formatSmartRange } from "@/utils";

// AmphoreLib's own FileViewer bundles a fallback pdf.worker path that
// resolves to a broken data: URI in ITS build (a library build has no
// fixed site root to emit a real worker asset against — see FileViewer's
// own comment). FileViewer only uses that fallback when this hasn't
// already been set, so setting it here — resolved correctly by Chronos's
// own app-mode Vite build — wins. Runs once at module import (this file
// only loads once the Reports view does), before FileViewer's own lazy
// `import("react-pdf")` can resolve and check this value.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface IReportSubmitModalProps {
	isOpen: boolean;
	onClose: () => void;
	reportId?: number;
	existingReport?: TSavedReport;
}

export const ReportSubmitModal: React.FC<IReportSubmitModalProps> = ({
	isOpen,
	onClose,
	reportId,
	existingReport,
}) => {
	const { filters } = useFiltersContext("reports");
	const { values, setFieldValue } = useFormikContext<TReportFormValues>();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	// Finalizing again would change nothing: the API keeps a finalized
	// report as it was frozen.
	const isFinalized = !!existingReport && !existingReport.isDraft;

	const [previewFile, setPreviewFile] = useState<File | null>(null);

	// "Report Weber — September – October 2026": who it is for, when.
	const defaultName = `${[
		t("reports.defaultName"),
		values.client?.name ?? filters?.client?.name ?? filters?.project?.name,
	]
		.filter(Boolean)
		.join(" ")} — ${formatSmartRange(filters?.dateFrom, filters?.dateTo)}`;
	const name = values.name?.trim() || defaultName;

	// Opened on a report with no name yet: offer one to edit rather
	// than an empty field. A draft keeps the name it was saved with.
	useEffect(() => {
		if (isOpen && !values.name?.trim()) setFieldValue("name", defaultName);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const buildSections = (projects: TReportFormProject[]): TReportSection[] =>
		projects
			.filter((project) => project.selected)
			.map((project) => ({
				name: project.name ?? "",
				tasks: (project.tasks ?? [])
					.map((task) => task.text)
					.filter((text) => text),
				days: parseFloat(project.days || "0"),
				// Kept with the section: a saved report shows its badges
				// without reaching back to the project.
				ticketPrefixes: project.ticketPrefixes ?? [],
				color: project.color,
			}));

	// Finalize = persist the report (locks it). No PDF render here: the user
	// already saw the aperçu, and the cached PDF is produced lazily on the
	// first download/generate afterwards.
	const handleFinalize = async (values: TReportFormValues) => {
		// A report covers a project or a client.
		if (!filters?.project?.id && !filters?.client?.id) {
			throw new Error("Report scope is missing");
		}
		if (!values.company?.id) {
			throw new Error(t("reports.submitModal.companyRequired"));
		}

		const sections = buildSections(values.projects);
		// The client billed; for a report by client, the one it is scoped on.
		const clientId = values?.client?.id ?? filters?.client?.id ?? null;

		if (reportId) {
			return ReportsServices.update(reportId, {
				companyId: values?.company?.id,
				clientId,
				workType: values?.workType?.code ?? null,
				...(existingReport?.isDraft && {
					name,
					projectId: filters?.project?.id ?? null,
					sections,
					isDraft: false,
				}),
			});
		} else {
			return ReportsServices.save({
				name,
				projectId: filters?.project?.id ?? null,
				companyId: values.company.id,
				clientId,
				workType: values?.workType?.code ?? null,
				sections,
				dateFrom: filters?.dateFrom ?? "",
				dateTo: filters?.dateTo ?? "",
				isDraft: false,
			});
		}
	};

	const submitMutation = useMutation({
		mutationKey: ["submitReport"],
		mutationFn: () => handleFinalize(values),
		onSuccess: (saved) => {
			queryClient.invalidateQueries({ queryKey: reportKeys.saved });
			if (reportId) {
				queryClient.invalidateQueries({
					queryKey: reportKeys.savedDetail(reportId),
				});
			}
			// Close both modals and keep the user on the report. A new one
			// moves to its own page: left on /reports/new, the form stayed
			// editable and a second Finalize saved a duplicate.
			closePreview();
			onClose();
			if (!reportId) navigate(`/reports/${saved.id}`, { replace: true });
		},
	});

	const previewMutation = useMutation({
		mutationFn: () => {
			if (!filters?.project?.id && !filters?.client?.id)
				throw new Error("Report scope is missing");
			return ReportsServices.preview({
				sections: buildSections(values.projects),
				projectId: filters.project?.id,
				company: values.company ?? undefined,
				client: values.client ?? filters.client ?? undefined,
				workType: values.workType?.code ?? null,
				dateFrom: filters.dateFrom,
				dateTo: filters.dateTo,
				filesToFetch: {
					companyLogo: values.company?.logo ?? null,
					clientLogo: values.client?.logo ?? null,
				},
			});
		},
	});

	const handlePreview = async () => {
		const response = await previewMutation.mutateAsync();
		setPreviewFile(
			new File([response.data], "report.pdf", {
				type: "application/pdf",
			})
		);
	};

	const closePreview = () => setPreviewFile(null);

	const isLoading = submitMutation.isPending || previewMutation.isPending;

	return (
		<>
			<Modal
				open={isOpen}
				onClose={() => {
					if (isLoading) return;
					onClose();
				}}
				size="md"
				title={t("reports.submitModal.title")}
				closeOnOverlayClick={!isLoading}
			>
				<div className="mb-6 flex flex-col gap-4">
					<Input
						label={t("reports.submitModal.name")}
						value={values.name ?? ""}
						onChange={(value) => setFieldValue("name", value)}
						placeholder={defaultName}
						disabled={isLoading}
					/>
					<CompanySelect
						isClearable={true}
						onChange={(value) => setFieldValue("company", value)}
						disabled={isLoading}
						value={values.company}
					/>
					<ClientSelect
						isClearable={true}
						onChange={(value) => setFieldValue("client", value)}
						disabled={isLoading}
						value={values.client}
					/>
					<WorkTypesSelect
						isClearable={true}
						onChange={(value) => setFieldValue("workType", value)}
						disabled={isLoading}
						value={values.workType}
					/>
				</div>
				<Button
					onClick={() => promiseToast(handlePreview())}
					className="w-full"
					isLoading={previewMutation.isPending}
					disabled={!values.company || submitMutation.isPending}
				>
					{t("reports.submitModal.preview")}
				</Button>
			</Modal>

			<Modal
				open={!!previewFile}
				onClose={closePreview}
				size="lg"
				title={t("reports.submitModal.preview")}
			>
				{previewFile && (
					<div className="-mt-4 h-[75vh]">
						<FileViewer src={previewFile} name="report.pdf" />
					</div>
				)}
				{!isFinalized && (
					<InfoMessage color="warning" className="mt-4">
						{t("reports.submitModal.finalizeWarning")}
					</InfoMessage>
				)}
				<div className="mt-4 flex justify-end gap-2">
					<Button
						color="white"
						onClick={closePreview}
						disabled={submitMutation.isPending}
					>
						{t("actions.cancel")}
					</Button>
					{!isFinalized && (
						<Button
							onClick={() =>
								promiseToast(submitMutation.mutateAsync())
							}
							isLoading={submitMutation.isPending}
							disabled={!values.company}
						>
							{t("reports.submitModal.finalize")}
						</Button>
					)}
				</div>
			</Modal>
		</>
	);
};
